'use client';

import { useState, useEffect } from 'react';

type AnimationType = 'blueprint' | 'construction' | 'morph' | 'dots';

interface LoadingAnimationProps {
  type?: AnimationType;
  className?: string;
  size?: 'sm' | 'md' | 'lg';
}

export function LoadingAnimation({
  type = 'blueprint',
  className = '',
  size = 'md',
}: LoadingAnimationProps) {
  const [animationStep, setAnimationStep] = useState(0);

  const sizeClasses = {
    sm: 'w-24 h-24',
    md: 'w-32 h-32',
    lg: 'w-48 h-48',
  };

  useEffect(() => {
    const interval = setInterval(() => {
      setAnimationStep((prev) => (prev + 1) % 4);
    }, 800);
    return () => clearInterval(interval);
  }, []);

  if (type === 'blueprint') {
    return (
      <div className={`${sizeClasses[size]} ${className} relative`}>
        <svg viewBox="0 0 100 100" className="w-full h-full">
          {/* Animated blueprint lines without grid */}
          <g className="stroke-teal-500" fill="none" strokeWidth="1.5">
            <path
              d="M 20 80 L 20 40 L 50 20 L 80 40 L 80 80 Z"
              strokeDasharray="200"
              strokeDashoffset={animationStep === 0 ? 200 : 0}
              className="transition-all duration-1000"
            />
            <path
              d="M 20 40 L 80 40"
              strokeDasharray="60"
              strokeDashoffset={animationStep < 1 ? 60 : 0}
              className="transition-all duration-1000 delay-500"
            />
            <rect
              x="35"
              y="55"
              width="30"
              height="25"
              strokeDasharray="110"
              strokeDashoffset={animationStep < 2 ? 110 : 0}
              className="transition-all duration-1000 delay-1000"
            />
            <path
              d="M 50 55 L 50 80"
              strokeDasharray="25"
              strokeDashoffset={animationStep < 3 ? 25 : 0}
              className="transition-all duration-1000 delay-1500"
            />
          </g>
        </svg>
      </div>
    );
  }

  if (type === 'construction') {
    return (
      <div className={`${sizeClasses[size]} ${className} relative`}>
        <svg viewBox="0 0 100 100" className="w-full h-full">
          {/* Foundation */}
          <rect
            x="20"
            y="80"
            width="60"
            height="10"
            fill="#6b7280"
            opacity={animationStep >= 0 ? 1 : 0}
            className="transition-opacity duration-500"
          />

          {/* Walls */}
          <rect
            x="20"
            y="40"
            width="10"
            height="40"
            fill="#9ca3af"
            opacity={animationStep >= 1 ? 1 : 0}
            className="transition-opacity duration-500 delay-500"
          />
          <rect
            x="70"
            y="40"
            width="10"
            height="40"
            fill="#9ca3af"
            opacity={animationStep >= 1 ? 1 : 0}
            className="transition-opacity duration-500 delay-500"
          />

          {/* Roof structure */}
          <polygon
            points="15,40 50,20 85,40"
            className="fill-primary transition-opacity duration-500 delay-1000"
            opacity={animationStep >= 2 ? 1 : 0}
          />

          {/* Windows and door */}
          <g
            opacity={animationStep >= 3 ? 1 : 0}
            className="transition-opacity duration-500 delay-1500"
          >
            <rect x="35" y="50" width="15" height="15" fill="#60a5fa" />
            <rect x="50" y="50" width="15" height="15" fill="#60a5fa" />
            <rect
              x="42.5"
              y="65"
              width="15"
              height="25"
              className="fill-primary"
            />
          </g>
        </svg>
      </div>
    );
  }

  if (type === 'morph') {
    const shapes = [
      'M 50 20 L 80 40 L 80 80 L 20 80 L 20 40 Z', // House
      'M 30 30 L 70 30 L 70 70 L 30 70 Z', // Square
      'M 50 20 L 75 35 L 75 65 L 50 80 L 25 65 L 25 35 Z', // Hexagon
      'M 50 15 L 85 50 L 50 85 L 15 50 Z', // Diamond
    ];

    return (
      <div className={`${sizeClasses[size]} ${className} relative`}>
        <svg viewBox="0 0 100 100" className="w-full h-full">
          <path
            d={shapes[animationStep]}
            fill="none"
            strokeWidth="2"
            className="stroke-primary transition-all duration-1000 ease-in-out"
          />
          <path
            d={shapes[animationStep]}
            opacity="0.1"
            className="fill-primary transition-all duration-1000 ease-in-out"
          />
        </svg>
      </div>
    );
  }

  // Default dots animation
  return (
    <div
      className={`${sizeClasses[size]} ${className} flex items-center justify-center`}
    >
      <div className="flex space-x-2">
        {[0, 1, 2].map((index) => (
          <div
            key={index}
            className={`w-3 h-3 bg-primary rounded-full animate-pulse`}
            style={{
              animationDelay: `${index * 200}ms`,
              opacity: animationStep === index ? 1 : 0.3,
            }}
          />
        ))}
      </div>
    </div>
  );
}
